/*
* +------------------------------------------------------------------------+
* | Licensed Materials - Property of IBM
* | IBM Cognos Products: Dashboard
* | (C) Copyright IBM Corp. 2017, 2021
* |
* | US Government Users Restricted Rights - Use, duplication or disclosure
* | restricted by GSA ADP Schedule Contract with IBM Corp.
* +------------------------------------------------------------------------+
*/

define(['underscore'], function(_) {


	/**
	 *@constructor
	 */
	var DeploymentReferencesUtil = {};

	DeploymentReferencesUtil.updateIds = function(spec, deploymentReferences, dashboardSearchPath) {
		if (!spec || !deploymentReferences || deploymentReferences.length === 0) {
			return;
		}

		DeploymentReferencesUtil.updateWidgetIds(spec, deploymentReferences);
		DeploymentReferencesUtil.updateDatasetShaping(spec, deploymentReferences);
		DeploymentReferencesUtil.updateDataSources(spec, deploymentReferences, dashboardSearchPath);
		DeploymentReferencesUtil.updateDrillThrough(spec, deploymentReferences);
	};

	DeploymentReferencesUtil.updateWidgetIds = function(spec, deploymentReferences) {
		if (!spec || !spec.widgets) {
			return;
		}

		_.keys(spec.widgets).forEach(function(key) {
			var widget = spec.widgets[key];
			if (widget.dataSet) {
				var reference = DeploymentReferencesUtil.findReference(widget.dataSet.id, deploymentReferences);
				if (reference) {
					widget.dataSet.id = reference.id;
					widget.dataSet.type = reference.type;
					widget.dataSet.name = reference.defaultName;
					widget.dataSet.exists = true;
					var ancestors = reference.ancestors;
					if (ancestors && ancestors.length > 0) {
						widget.dataSet.ancestors = reference.ancestors;
						widget.dataSet.parentFolderURL = ancestors[ancestors.length - 1]._meta.links.self.url;
					}
				}
			}
		});
	};

	DeploymentReferencesUtil.updateDatasetShaping = function(spec, deploymentReferences) {
		if (!spec || !spec.datasetShaping) {
			return;
		}

		spec.datasetShaping.forEach(function(datasetShaping) {
			var reference = DeploymentReferencesUtil.findReference(datasetShaping.id, deploymentReferences);
			if (reference) {
				datasetShaping.id = reference.id;
			}
		});
	};

	DeploymentReferencesUtil.updateDataSources = function(spec, deploymentReferences, dashboardSearchPath) {
		if (!spec || !spec.dataSources || !spec.dataSources.sources) {
			return;
		}

		spec.dataSources.sources.forEach(function(source) {
			var ref = DeploymentReferencesUtil.findReference(source.assetId, deploymentReferences);
			if (ref) {
				source.assetId = ref.id;
			}
			ref = DeploymentReferencesUtil.findReference(source.assetId, deploymentReferences, true /*ignoreIdMatchCheck*/);
			if (ref) {
				source.searchPath = ref.searchPath;
			}
			if (source.shaping) {
				DeploymentReferencesUtil.updateShaping(source.shaping, deploymentReferences, dashboardSearchPath);
			}
		});
	};

	DeploymentReferencesUtil.updateShaping = function(shaping, deploymentReferences, dashboardSearchPath) {
		if (!shaping) {
			return;
		}

		if (shaping.moserJSON) {
			var ref = DeploymentReferencesUtil.findReference(shaping.moserJSON.useSpec[0].storeID, deploymentReferences);
			if (ref) {
				shaping.moserJSON.useSpec[0].storeID = ref.id;
			}
		}

		if (shaping.embeddedModuleId) {
			var embededModuleRef = DeploymentReferencesUtil.findReference(shaping.embeddedModuleId, deploymentReferences, /*ignoreIdmatch*/true);
			if (embededModuleRef) {
				if (dashboardSearchPath && dashboardSearchPath.length &&
					embededModuleRef.searchPath && embededModuleRef.searchPath.indexOf(dashboardSearchPath)!==0) {
					//NOTE: Shouldn't happen going forward but enforce this rule.
					//EmbeddedModules in deployment references must be inside the dashboard.
					this.resetEmbeddedModule(shaping);
				} else if (shaping.embeddedModuleId !== embededModuleRef.id) {
					//When the dashboard is copied, the EmbeddedModuleId in the spec is referencing the pre-copied id
					//(which is also saved in the "name" field in the deploymentReferences)
					//Update the embeddedModuleId to match the new id in the copy.
					//The name will be synched to the id on Save (see EmbeddedModuleManager)
					shaping.embeddedModuleId = embededModuleRef.id;
				}
			} else {
				//NOTE: Shouldn't happen going forward but enforce this rule.
				//If, on open, there is no deployment reference name that matches the
				//embeddedModuleId for a given shaping, its stale..reset it.
				this.resetEmbeddedModule(shaping);
			}
		}
	};

	DeploymentReferencesUtil.resetEmbeddedModule = function(shaping) {
		//Reset the embedded module references in shaping to ensure the module is updated.
		//This should only happen in rare conditions
		//where deployment references are incomplete or invalid.
		delete shaping.embeddedModuleId;
		delete shaping.embeddedModuleName;
		shaping.embeddedModuleUpToDate = false;
	};

	DeploymentReferencesUtil.updateDrillThrough = function(spec, deploymentReferences) {
		if (!spec || !spec.drillThrough || !spec.drillThrough.length) {
			return;
		}

		spec.drillThrough.forEach(function(drillThroughDefinition) {
			var drillEntryRef = DeploymentReferencesUtil.findReference(drillThroughDefinition.assetId, deploymentReferences);
			if (drillEntryRef) {
				drillThroughDefinition.assetId = drillEntryRef.id;
			}
		});
	};

	DeploymentReferencesUtil.findReference = function(id, deploymentReferences, ignoreIdMatchCheck) {
		if (!deploymentReferences) {
			return null;
		}
		for (var i = 0; i < deploymentReferences.length; i++) {
			var ref = deploymentReferences[i];
			const refNameKey = ref.name && _.keys(ref.name)[0] || 'en-us';
			if (ref.objects && ref.objects.length > 0 && ref.name[refNameKey] === id) {
				if (ignoreIdMatchCheck || (ref.objects[0].id !== id)) {
					return ref.objects[0];
				}
				return null;
			}
		}
		return null;
	};

	return DeploymentReferencesUtil;
});
